/* eslint-disable react/require-default-props */
/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Collapse,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getCurrency } from '../../../utils/general';
import {
  decimalsToHours,
  decimalsToHoursWithSeconds,
  formatMillisecondsToHHMM,
  calculateWorkedHoursDecimal,
  formatDecimalHours,
  decimalsToHHMMDecimal
} from '../../../utils/calendarHelper';
import CustomTooltip from '../../../components/shared/CustomTooltip';
import { theme } from '../../../theme/themeConfig';
import { ReactComponent as ChevronUpIcon } from '../../../assets/icons/chevron-up.svg';
import { ReactComponent as ChevronDownIcon } from '../../../assets/icons/chevron-down.svg';

export interface TechnicianInformation {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
}

export enum ProjectType {
  AdHoc = 'Ad-Hoc',
  Regular = 'Regular'
}

export interface OriginalTaskElement {
  id: number;
  name: string;
}

interface DataList {
  actualEnd: string;
  actualStart: string;
  actualTotal: number;
  clientName: string;
  closedBy: string;
  day: string;
  id: number;
  laborHourCost: string;
  laborTotalCost: string;
  millisecondsWorked: string;
  originalTask: OriginalTaskElement[];
  pauseIterations?: any[];
  projectName?: string;
  projectType: ProjectType;
  scheduledEnd: string;
  scheduledStart: string;
  scheduledTotal: number;
  serviceName?: string;
  startedBy: null;
  status: string;
  taskName: string;
  technician: string;
  technicianInformation: TechnicianInformation;
  totalPauseMs?: number;
  travelTime: string;
  travelTimeInMilliseconds: string;
  type: string;
  typeText: string;
  variance: number;
  varianceActual: string;
  variancePercentage: string;
}

interface Props {
  byDate: boolean;
  data: {
    dataList: DataList[];
    email: string;
    id: string;
    technician: string;
    technicianPaymentTotal: number;
    totalHoursScheduled: number;
    totalHoursWorked: string;
    totalPauseMs?: number;
  }[];
  isTechnician?: boolean;
}

function ListServiceAndTasks({ dataRow }: { dataRow: any }) {
  const { t } = useTranslation();
  return (
    <Box display="flex" flexDirection="column" padding="0px 10px 10px 10px">
      {dataRow?.map((item: any) => (
        <Typography fontSize={15} key={item.id} variant="body1">
          <strong>{`${t('TIMESHEET.service')}: `}</strong>
          {`${item.serviceName || t('TIMESHEET.noData')}`} <br />
          <strong>{`${t('TIMESHEET.task')}: `}</strong>
          {`${
            item.projectType === 'Regular'
              ? item.originalTask
                  .map((element: any) => element.name)
                  ?.join(', ') || t('TIMESHEET.noData')
              : item.originalTask || t('TIMESHEET.noData')
          }`}
        </Typography>
      ))}
    </Box>
  );
}

const TimesheetReportTable: React.FC<Props> = function TimesheetReportTable({
  data,
  byDate,
  isTechnician = false
}) {
  const { t } = useTranslation();
  const [openRow, setOpenRow] = useState<number | null>(null);

  const taskByDate =
    data
      ?.flatMap((technicianTasks) =>
        technicianTasks?.dataList?.map((task) => ({
          ...task,
          ...technicianTasks
        }))
      )
      .sort((a, b) => {
        return new Date(a.day).getTime() - new Date(b.day).getTime();
      }) || [];

  const toggleRow = (index: number) => {
    if (openRow === index) {
      setOpenRow(null);
      return;
    }
    setOpenRow(index);
  };

  return (
    <div>
      {data.length > 0 && (
        <TableContainer
          component={Paper}
          style={{ marginTop: '20px', overflowX: 'scroll' }}
        >
          {byDate && (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {!isTechnician && (
                    <TableCell align="center">
                      {`${t('TIMESHEET.technician')}`}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    {`${t('TIMESHEET.item')}`}
                  </TableCell>
                  <TableCell align="center">
                    {`${t('TIMESHEET.date')}`}
                  </TableCell>
                  <TableCell align="center">
                    {`${t('TIMESHEET.type')}`}
                  </TableCell>
                  <TableCell align="center">
                    {`${t('TIMESHEET.pauseTime')}`}
                    <br />
                    {`${t('TIMESHEET.hhmm')}`}
                  </TableCell>
                  <TableCell align="center">
                    {`${t('TIMESHEET.totalHours')}`}
                    <br />
                    hh:mm:ss
                  </TableCell>
                  <TableCell align="center">
                    {`${t('TIMESHEET.totalHours')}`}
                    <br />
                    (Decimal)
                  </TableCell>
                  <TableCell align="center">HH:MM (Decimals)</TableCell>
                  <TableCell align="center">
                    {`${t('TIMESHEET.totalCost')}`}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taskByDate.map((item) => (
                  <TableRow>
                    {!isTechnician && (
                      <TableCell align="center">{item?.technician}</TableCell>
                    )}

                    <TableCell align="center">
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          gap: 0.5,
                          justifyContent: 'center'
                        }}
                      >
                        {(item as any)?.isSR ? (
                          <Box
                            component="span"
                            sx={{
                              background: '#EDE9FE',
                              borderRadius: '4px',
                              color: '#7C3AED',
                              fontSize: '11px',
                              fontWeight: 700,
                              mr: 0.5,
                              px: '6px',
                              py: '2px'
                            }}
                          >
                            ⚡ SR
                          </Box>
                        ) : null}
                        {`${item?.projectName}, ${item?.taskName}`}
                        <CustomTooltip
                          backgroundColor={theme.palette.grey[600]}
                          borderColor={theme.palette.grey[600]}
                          component={<ListServiceAndTasks dataRow={[item]} />}
                        >
                          <span
                            style={{
                              display: 'inline-flex'
                            }}
                          >
                            <i
                              className="custom-icon icon-info"
                              style={{
                                color: `${theme.palette.informative[600]}`,
                                fontSize: 21
                              }}
                            />
                          </span>
                        </CustomTooltip>
                      </Box>
                    </TableCell>

                    <TableCell align="center">{item?.day}</TableCell>
                    <TableCell align="center">
                      <Typography variant="primary">
                        {item?.typeText?.toLowerCase() === 'regular'
                          ? t('LEAD.CREATE.GENERAL_INFORMATION.regular')
                          : t('LEAD.CREATE.GENERAL_INFORMATION.adhoc')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const actualHours = item?.actualTotal || 0;
                        const workedHours = calculateWorkedHoursDecimal(
                          actualHours,
                          item?.pauseIterations
                        );
                        const pauseHours = actualHours - workedHours;
                        return pauseHours > 0
                          ? decimalsToHours(pauseHours)
                          : '00:00:00';
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      {decimalsToHoursWithSeconds(
                        calculateWorkedHoursDecimal(
                          item?.actualTotal,
                          item?.pauseIterations
                        )
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {formatDecimalHours(
                        calculateWorkedHoursDecimal(
                          item?.actualTotal,
                          item?.pauseIterations
                        ),
                        4
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {decimalsToHHMMDecimal(
                        calculateWorkedHoursDecimal(
                          item?.actualTotal,
                          item?.pauseIterations
                        )
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {getCurrency(item?.laborTotalCost)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!byDate &&
            data.map((item, index) => (
              <>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="left"
                        colSpan={5}
                        onClick={() => toggleRow(index)}
                      >
                        <Box
                          alignItems="center"
                          display="flex"
                          justifyContent="space-between"
                          mr="16px"
                          width="100%"
                        >
                          <Box display="flex" width="100%">
                            <Box display="flex" sx={{ my: '20px' }}>
                              {openRow === index ? (
                                <ChevronUpIcon height="40px" width="auto" />
                              ) : (
                                <ChevronDownIcon height="40px" width="auto" />
                              )}
                            </Box>
                            <Typography
                              my="20px"
                              paddingLeft="18px"
                              variant="h6"
                            >
                              {`Technician: ${item?.technician}`}
                            </Typography>
                          </Box>
                          <Box display="flex" textAlign="right" width="100%">
                            <Typography
                              paddingRight="18px"
                              variant="h6"
                              width="100%"
                            >
                              {`${t('TIMESHEET.totalCost')}: ${getCurrency(
                                item?.technicianPaymentTotal?.toFixed(2) || 0
                              )}`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                </Table>
                <Collapse unmountOnExit in={openRow === index} timeout="auto">
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">
                          {`${t('TIMESHEET.item')}`}
                        </TableCell>
                        <TableCell align="center">
                          {`${t('TIMESHEET.date')}`}
                        </TableCell>
                        <TableCell align="center">
                          {`${t('TIMESHEET.type')}`}
                        </TableCell>
                        <TableCell align="center">
                          {`${t('TIMESHEET.pauseTime')}`}
                          <br />
                          {`${t('TIMESHEET.hhmm')}`}
                        </TableCell>
                        <TableCell align="center">
                          {`${t('TIMESHEET.totalHours')}`}
                          <br />
                          hh:mm:ss
                        </TableCell>
                        <TableCell align="center">
                          {`${t('TIMESHEET.totalHours')}`}
                          <br />
                          (Decimal)
                        </TableCell>
                        <TableCell align="center">HH:MM (Decimals)</TableCell>
                        <TableCell align="center">
                          {`${t('TIMESHEET.totalCost')}`}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.dataList.map((val) => (
                        <TableRow key={val.id}>
                          <TableCell align="center">
                            <Box
                              sx={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: 0.5,
                                justifyContent: 'center'
                              }}
                            >
                              {(val as any)?.isSR ? (
                                <Box
                                  component="span"
                                  sx={{
                                    background: '#EDE9FE',
                                    borderRadius: '4px',
                                    color: '#7C3AED',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    mr: 0.5,
                                    px: '6px',
                                    py: '2px'
                                  }}
                                >
                                  ⚡ SR
                                </Box>
                              ) : null}
                              {`${val?.projectName}, ${val?.taskName}`}{' '}
                              <CustomTooltip
                                backgroundColor={theme.palette.grey[600]}
                                borderColor={theme.palette.grey[600]}
                                component={
                                  <ListServiceAndTasks dataRow={[val]} />
                                }
                              >
                                <span
                                  style={{
                                    display: 'inline-flex'
                                  }}
                                >
                                  <i
                                    className="custom-icon icon-info"
                                    style={{
                                      color: `${theme.palette.informative[600]}`,
                                      fontSize: 21
                                    }}
                                  />
                                </span>
                              </CustomTooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{val?.day}</TableCell>
                          <TableCell align="center">{val.typeText}</TableCell>
                          <TableCell align="center">
                            {(() => {
                              const actualHours = val?.actualTotal || 0;
                              const workedHours = calculateWorkedHoursDecimal(
                                actualHours,
                                val?.pauseIterations
                              );
                              const pauseHours = actualHours - workedHours;
                              return pauseHours > 0
                                ? decimalsToHours(pauseHours)
                                : '00:00:00';
                            })()}
                          </TableCell>
                          <TableCell align="center">
                            {decimalsToHoursWithSeconds(
                              calculateWorkedHoursDecimal(
                                val?.actualTotal,
                                val?.pauseIterations
                              )
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {formatDecimalHours(
                              calculateWorkedHoursDecimal(
                                val?.actualTotal,
                                val?.pauseIterations
                              ),
                              4
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {decimalsToHHMMDecimal(
                              calculateWorkedHoursDecimal(
                                val?.actualTotal,
                                val?.pauseIterations
                              )
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {getCurrency(val?.laborTotalCost)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} />
                        <TableCell align="center">
                          <Typography fontSize={16} variant="h6">
                            {`${t('TIMESHEET.totalPause')}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontSize={16} variant="h6">
                            {`${t('TIMESHEET.totalHours')}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontSize={16} variant="h6">
                            {`${t('TIMESHEET.totalHours')}`} (Decimal)
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontSize={16} variant="h6">
                            HH:MM (Decimals)
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontSize={16} variant="h6">
                            {`${t('TIMESHEET.totalCost')}`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={3} />
                        <TableCell align="center">
                          {item?.totalPauseMs
                            ? formatMillisecondsToHHMM(
                                item.totalPauseMs.toString()
                              )
                            : '00:00:00'}
                        </TableCell>
                        <TableCell align="center">
                          {decimalsToHoursWithSeconds(
                            calculateWorkedHoursDecimal(
                              parseFloat(item?.totalHoursWorked) || 0,
                              item?.dataList?.flatMap(
                                (task: any) => task.pauseIterations || []
                              )
                            )
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {formatDecimalHours(
                            calculateWorkedHoursDecimal(
                              parseFloat(item?.totalHoursWorked) || 0,
                              item?.dataList?.flatMap(
                                (task: any) => task.pauseIterations || []
                              )
                            ),
                            4
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {decimalsToHHMMDecimal(
                            calculateWorkedHoursDecimal(
                              parseFloat(item?.totalHoursWorked) || 0,
                              item?.dataList?.flatMap(
                                (task: any) => task.pauseIterations || []
                              )
                            )
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {getCurrency(
                            item?.technicianPaymentTotal?.toFixed(2) || 0
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Collapse>
              </>
            ))}
        </TableContainer>
      )}
    </div>
  );
};

export default TimesheetReportTable;
